/**
 * EGLO Download Center - Custom Element
 *
 * Fetches documents directly from the EGLO Lobster API,
 * renders results, and manages document type filtering.
 */

if (!customElements.get('download-center')) {
  customElements.define(
    'download-center',
    class DownloadCenter extends HTMLElement {
      constructor() {
        super();

        this._apiBaseUrl = this.dataset.apiBaseUrl || '';
        this._shopId = this.dataset.apiShopId || 'eglo-prod';
        this._apiUser = this.dataset.apiUser || '';
        this._apiPassword = this.dataset.apiPassword || '';
        this._locale = this.dataset.locale || 'de';
        this._downloadProxy = (this.dataset.downloadProxy || '/apps/storyblok-sync/download').replace(/\/$/, '');
        this._currentArticleNumber = null;
        this._allDocuments = [];

        this._form = this.querySelector('#download-center-form');
        this._articleInput = this.querySelector('#download-center-article-number');
        this._typeFilter = this.querySelector('#download-center-doc-type');
        this._typeFilterWrapper = this.querySelector('.download-center__type-filter');

        this._loadingEl = this.querySelector('.download-center__loading');
        this._emptyEl = this.querySelector('.download-center__empty');
        this._errorEl = this.querySelector('.download-center__error');
        this._errorMessageEl = this.querySelector('.download-center__error-message');

        this._resultsEl = this.querySelector('.download-center__results');
        this._resultsCountEl = this.querySelector('.download-center__results-count');
        this._gridEl = this.querySelector('.download-center__grid');

        this._bindEvents();
      }

      _bindEvents() {
        this._form?.addEventListener('submit', (event) => {
          event.preventDefault();
          const articleNumber = this._articleInput?.value?.trim();
          if (articleNumber) {
            this._search(articleNumber);
          }
        });

        this._typeFilter?.addEventListener('change', () => {
          this._applyFilter();
        });

        this._gridEl?.addEventListener('click', (event) => {
          const button = event.target.closest('.download-center__download-btn');
          if (!button) return;
          event.preventDefault();
          this._downloadFile(button.dataset.url, button.dataset.filename, button);
        });
      }

      async _search(articleNumber) {
        this._currentArticleNumber = articleNumber;
        this._setState('loading');

        if (!this._apiBaseUrl || !this._apiUser || !this._apiPassword) {
          this._setState(
            'error',
            window.downloadCenterStrings?.errorApiNotConfigured ||
              'The download API is not configured. Add the API credentials in the section settings.',
          );
          return;
        }

        const apiLocale = this._locale.toUpperCase();
        const apiUrl = `${this._apiBaseUrl}/${encodeURIComponent(this._shopId)}/${encodeURIComponent(articleNumber)}/${encodeURIComponent(apiLocale)}`;

        let rawData;
        try {
          const response = await fetch(apiUrl, {
            headers: {
              Authorization: 'Basic ' + btoa(`${this._apiUser}:${this._apiPassword}`),
              Accept: 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          rawData = await response.json();
        } catch (err) {
          this._setState('error', window.downloadCenterStrings?.errorGeneral || 'An error occurred. Please try again.');
          return;
        }

        const mediaLinks = Array.isArray(rawData) ? rawData : (rawData?.MediaLinks ?? []);

        this._allDocuments = mediaLinks.map((item) => ({
          mediaType: item.MediaType ?? '',
          mediaTypeDescriptionEN: item.MediaTypeDescriptionEN ?? '',
          mediaTypeDescriptionLocalized: item.MediaTypeDescriptionLocalized ?? '',
          mediaVariant: item.MediaVariant ?? '',
          mediaUrl: item.MediaURL ?? '',
          filename: item.Filename ?? '',
          locale: item.Locale ?? '',
          lastChanged: item.lastchangedTimestamp ?? '',
        }));

        if (this._allDocuments.length === 0) {
          this._setState('empty');
          return;
        }

        this._populateTypeFilter(this._allDocuments);
        this._setState('results');
        this._applyFilter();
      }

      _populateTypeFilter(documents) {
        const seen = new Set();
        const options = [];

        for (const doc of documents) {
          if (!seen.has(doc.mediaType)) {
            seen.add(doc.mediaType);
            options.push({
              value: doc.mediaType,
              label: this._getDocTypeLabel(doc),
            });
          }
        }

        const allLabel = this._typeFilter?.querySelector('option[value=""]');
        this._typeFilter.innerHTML = '';

        const defaultOption = document.createElement('option');
        defaultOption.value = '';
        defaultOption.textContent = allLabel?.textContent || 'All';
        this._typeFilter.appendChild(defaultOption);

        for (const opt of options) {
          const option = document.createElement('option');
          option.value = opt.value;
          option.textContent = opt.label;
          this._typeFilter.appendChild(option);
        }

        const hasMultipleTypes = options.length > 1;
        this._typeFilterWrapper?.toggleAttribute('hidden', !hasMultipleTypes);
        this._typeFilterWrapper?.setAttribute('aria-hidden', String(!hasMultipleTypes));
        this._typeFilter.value = '';
      }

      _applyFilter() {
        const selectedType = this._typeFilter?.value || '';
        const filtered = selectedType
          ? this._allDocuments.filter((doc) => doc.mediaType === selectedType)
          : this._allDocuments;

        this._renderResults(filtered);
      }

      _renderResults(documents) {
        const countText = this._buildCountText(documents.length);
        this._resultsCountEl.textContent = countText;

        this._gridEl.innerHTML = '';

        for (const doc of documents) {
          this._gridEl.appendChild(this._createCard(doc));
        }
      }

      _createCard(doc) {
        const card = document.createElement('div');
        card.className = 'download-center__card';

        const isImage = this._isImageUrl(doc.mediaUrl);
        const typeLabel = this._getDocTypeLabel(doc);
        const formattedDate = this._formatDate(doc.lastChanged);

        card.innerHTML = `
          <div class="download-center__card-preview">
            ${isImage
              ? `<img class="download-center__card-thumbnail" src="${this._escapeAttr(doc.mediaUrl)}" alt="${this._escapeAttr(doc.filename)}" loading="lazy" width="80" height="80">`
              : `<div class="download-center__card-icon" aria-hidden="true">${this._getFileIcon(doc)}</div>`
            }
          </div>
          <div class="download-center__card-body">
            <p class="download-center__card-type">${this._escapeHtml(typeLabel)}</p>
            <p class="download-center__card-filename">${this._escapeHtml(doc.filename)}</p>
            ${formattedDate ? `<p class="download-center__card-date">${this._escapeHtml(formattedDate)}</p>` : ''}
          </div>
          <div class="download-center__card-action">
            <button
              type="button"
              class="button button--sm download-center__download-btn"
              data-url="${this._escapeAttr(doc.mediaUrl)}"
              data-filename="${this._escapeAttr(doc.filename)}"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              <span>${this._escapeHtml(window.downloadCenterStrings?.download || 'Download')}</span>
            </button>
          </div>
        `;

        return card;
      }

      _getDownloadHost(mediaUrl) {
        try {
          return new URL(mediaUrl).hostname;
        } catch {
          return '';
        }
      }

      /** tools.eglo.com datasheets expose CORS for EGLO storefront origins. */
      _canFetchDirectly(mediaUrl) {
        return this._getDownloadHost(mediaUrl) === 'tools.eglo.com';
      }

      /** CDN assets have no CORS — must use the Storyblok Sync App Proxy. */
      _needsDownloadProxy(mediaUrl) {
        return this._getDownloadHost(mediaUrl) === 'eglo.contentdeliveryhub.net';
      }

      async _downloadFile(url, filename, triggerEl) {
        if (!url) return;

        const safeName = this._resolveDownloadFilename(filename, url);
        const labelEl = triggerEl?.querySelector('span');
        const defaultLabel = labelEl?.textContent || window.downloadCenterStrings?.download || 'Download';
        const loadingLabel = window.downloadCenterStrings?.downloading || 'Downloading…';

        triggerEl?.setAttribute('aria-busy', 'true');
        if (triggerEl) triggerEl.disabled = true;
        if (labelEl) labelEl.textContent = loadingLabel;

        try {
          const blob = await this._fetchFileBlob(url);
          this._saveBlob(blob, safeName);
        } catch {
          if (this._needsDownloadProxy(url)) {
            this._triggerProxyDownload(url, safeName);
          } else if (this._canFetchDirectly(url)) {
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        } finally {
          triggerEl?.removeAttribute('aria-busy');
          if (triggerEl) triggerEl.disabled = false;
          if (labelEl) labelEl.textContent = defaultLabel;
        }
      }

      _buildProxyDownloadUrl(mediaUrl, filename) {
        const params = new URLSearchParams({
          url: mediaUrl,
          filename: filename || 'download',
        });
        return `${this._downloadProxy}?${params.toString()}`;
      }

      async _fetchFileBlob(mediaUrl) {
        if (this._canFetchDirectly(mediaUrl)) {
          const response = await fetch(mediaUrl, { credentials: 'omit', mode: 'cors' });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          return await response.blob();
        }

        if (!this._needsDownloadProxy(mediaUrl)) {
          throw new Error('Unsupported download host');
        }

        if (!this._downloadProxy) {
          throw new Error('Download proxy not configured');
        }

        const proxyUrl = this._buildProxyDownloadUrl(
          mediaUrl,
          this._resolveDownloadFilename('', mediaUrl),
        );
        const response = await fetch(proxyUrl);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        return await response.blob();
      }

      _triggerProxyDownload(mediaUrl, filename) {
        const link = document.createElement('a');
        link.href = this._buildProxyDownloadUrl(mediaUrl, filename);
        link.download = filename;
        link.rel = 'noopener';
        link.hidden = true;
        this.appendChild(link);
        link.click();
        link.remove();
      }

      _saveBlob(blob, filename) {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.rel = 'noopener';
        link.hidden = true;
        this.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      }

      _resolveDownloadFilename(filename, url) {
        if (filename) return filename;

        try {
          const pathname = new URL(url).pathname;
          const lastSegment = pathname.split('/').pop();
          if (lastSegment) return decodeURIComponent(lastSegment);
        } catch {
          /* ignore */
        }

        return 'download';
      }

      _setState(state, message = '') {
        this._loadingEl?.setAttribute('hidden', '');
        this._loadingEl?.setAttribute('aria-hidden', 'true');
        this._emptyEl?.setAttribute('hidden', '');
        this._errorEl?.setAttribute('hidden', '');
        this._resultsEl?.setAttribute('hidden', '');

        switch (state) {
          case 'loading':
            this._loadingEl?.removeAttribute('hidden');
            this._loadingEl?.removeAttribute('aria-hidden');
            break;
          case 'empty':
            this._emptyEl?.removeAttribute('hidden');
            break;
          case 'error':
            if (this._errorMessageEl) this._errorMessageEl.textContent = message;
            this._errorEl?.removeAttribute('hidden');
            break;
          case 'results':
            this._resultsEl?.removeAttribute('hidden');
            break;
        }
      }

      _getDocTypeLabel(doc) {
        if (doc.mediaTypeDescriptionLocalized && doc.mediaTypeDescriptionLocalized !== 'TODO') {
          return doc.mediaTypeDescriptionLocalized;
        }
        return doc.mediaTypeDescriptionEN || doc.mediaType;
      }

      _buildCountText(count) {
        const strings = window.downloadCenterStrings || {};
        if (count === 1) {
          return (strings.resultsCountOne || '{{ count }} document found').replace('{{ count }}', count);
        }
        return (strings.resultsCountOther || '{{ count }} documents found').replace('{{ count }}', count);
      }

      _isImageUrl(url) {
        if (!url) return false;
        return /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(url) || url.includes('contentdeliveryhub.net');
      }

      _getFileIcon(doc) {
        const ext = (doc.filename || '').split('.').pop()?.toLowerCase();
        if (ext === 'pdf') {
          return `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>`;
        }
        return `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>`;
      }

      _formatDate(timestamp) {
        if (!timestamp) return '';
        try {
          const date = new Date(timestamp);
          if (isNaN(date.getTime())) return '';
          return date.toLocaleDateString(this._locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
        } catch {
          return '';
        }
      }

      _escapeHtml(str) {
        return String(str ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      _escapeAttr(str) {
        return String(str ?? '').replace(/"/g, '&quot;');
      }
    },
  );
}
